import { EDITOR_ACTIONS, SCENE_OBJECT_TYPES } from "../../constants/SceneObjects";

const axios = require('axios');

const rolesWithDisabledToggles = ['user', 'creator'];
export const UNLIMITED = -1;
const adminRole = 'admin';

export const PLAN_CODE = {
  GUEST: 'guest',
  USER: 'user',
  PERFORMER: 'performer',
  BUSINESS: 'business',
  BUSINESS_TEAM: 'business_team',
}

/**
 * Feature names enum - JavaScript equivalent of FeatureNameEnum.php
 * Use this to access feature keys consistently across the application
 */
export const FEATURE_NAMES = {
    TRANSLATE_SUBTITLE_PUBLIC_ROOM: 'translate-subtitle-public-room',
    AGENCY_SERVICE_DISCOUNT: 'agency-service-discount',
    SUCCESS_MANAGER: 'success-manager',
    CHAT_SUPPORT: 'chat-support',
    TRANSLATE_SUBTITLE_TOURS: 'translate-subtitle-tours',
    SHOW_SUBTITLES: 'show-subtitles',
    TOURS_SCREENSHARE: 'tours-screenshare',
    RUN_PREMIUM_TOURS: 'run-premium-tours',
    CHESS_GAME: 'chess-game',
    VIEW_ALL_INTERACTIONS: 'view-all-interactions',
    INVITE_JUMP_TO_USER: 'invite-jump-to-user',
    TAKE_SEAT: 'take-seat',
    ONE_TO_ONE_CHAT: '1:1-chat',
    BUDDY_LIST: 'buddy-list',
    VIDEO_CALLS: 'video-calls',
    TEAM_MEMBERS: 'team-members',
    CONTENT_RIGHTS: 'content-rights',
    CREATE_PREMIUM_SPACES: 'create-premium-spaces',
    CREATE_PUBLIC_SPACES: 'create-public-spaces-new',
    UNLIMITED_SCREENSHARE: 'unlimited-screenshare',
    LOCK_ROOM: 'lock-room',
    INVITE_TO_CHAIR: 'invite-to-chair',
    RUN_UNLIMITED_TOURS: 'run-unlimited-tours',
    RUN_PRIVATE_TOURS: 'run-private-tours',
    RUN_ONE_TO_ONE_TOUR: 'run-1:1-tour',
    CUSTOM_LOGO: 'custom-logo',

    // Helper method to get all feature names as an array
    all: function () {
        return Object.values(this).filter(value => typeof value === 'string');
    }
};

// Cache for user features to avoid multiple API calls
let userFeaturesCache = null;
let userFeaturesCacheExpiry = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Generate request options for axios
 * @param {string} url - API endpoint URL
 * @param {string} method - HTTP method (get, post, etc.)
 * @param {Object} params - URL parameters
 * @returns {Object} Axios request configuration
 */
const generateRequestOpts = (url, method = 'get', params) => {
    return {
        method: method,
        url: url,
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            'X-CSRF-Token': window.yii && window.yii.getCsrfToken ? window.yii.getCsrfToken() : ''
        },
        params
    };
};

/**
 * Fetch user features from the server
 * @returns {Promise<Object>} Promise resolving to user features
 */
export const fetchUserFeatures = async () => {
    // Check if we have a valid cache
    const now = new Date().getTime();
    if (userFeaturesCache && userFeaturesCacheExpiry && now < userFeaturesCacheExpiry) {
        return userFeaturesCache;
    }

    try {
        const config = generateRequestOpts('/worlds/get-user-features');
        const response = await axios(config);

        if (response.data && response.data.success && response.data.features) {
            // Cache the features
            userFeaturesCache = response.data.features;
            userFeaturesCacheExpiry = now + CACHE_DURATION;
            return response.data.features;
        } else {
            console.error('Error fetching user features:', response.data?.message || 'Unknown error');
            return null;
        }
    } catch (error) {
        console.error('Error fetching user features:', error);
        return null;
    }
};

/**
 * Clear the user features cache
 */
export const clearUserFeaturesCache = () => {
    userFeaturesCache = null;
    userFeaturesCacheExpiry = null;
};

/**
 * Check if a specific feature is available for the current user
 * @param {string} featureName - The name of the feature to check
 * @returns {Promise<Object>} Promise resolving to feature details with availability, description, and value
 */
export const checkUserFeature = async (featureName) => {
    const features = await fetchUserFeatures();

    if (!features || !features[featureName]) {
        return {
            availability: false,
            description: null,
            value: null
        };
    }

    return features[featureName];
};

export const roleConstraints = (role, subscriptionPlan) => {
    const constraints = {
        'rooms': 3,
        'locations': 5,

        'tourScreenShare': 60,
        'guestSeat': 6,
        'screenStream': 1,
        'webLink': 10,
        'sceneChange': 5,
        'youtube': 10,
        'chessGame': 3,
        'baglessRoom': 10,
        'infoElement': 10,
        'vrInvisibleAction': 10,
        'vrMusicObject': 10,
        'payPalDonation': 1,
        'embeddedWebpage': 0,
        'profile': 0,
        'shopLink': 0,

        'paidLocation': false,
        'publicStage': false,
    }
    Object.keys(constraints).forEach(key => {
        constraints[key] = 100;
    })

    if (subscriptionPlan) {
        constraints.rooms = subscriptionPlan.number_of_locations || constraints.rooms;
        constraints.locations = subscriptionPlan.number_of_rooms || constraints.locations;
        constraints.guestSeat = subscriptionPlan.chairs_per_location || constraints.guestSeat;
    }

    return constraints;

    switch (role) {
        case 'user_role':
            return constraints;
        case 'creator_role':
            return { ...constraints, rooms: 10, locations: 10 }
        case 'business_role':
            return { ...constraints, rooms: 10, locations: 10, profile: 1, guestSeat: 15, shopLink: 3, embeddedWebpage: 10, paidLocation: 1, publicStage: 1 }
        case 'admin_role':
            return { ...constraints, rooms: 10, locations: 10, profile: 1000000, guestSeat: 15, shopLink: 1000000, embeddedWebpage: 15, paidLocation: true, publicStage: true }
        default: return false;
    }
}

export const countSceneElements = (elType, sceneObj) => {
    let count = 0;
    for (let x = 0; x < sceneObj.length; x++) {
        if (sceneObj[x].type === elType) {
            count++;
        }
    }
    return count;
}

export const countStates = (stateType, scenes) => {
    let count = 0;
    for (let x = 0; x < scenes.length; x++) {
        if (stateType === 'locationWithPayment') {
            if (scenes[x].hasOwnProperty('locationWithPayment')) {
                if (scenes[x].locationWithPayment.state === true) {
                    count++;
                }
            }
        } else {
            if (scenes[x][stateType] === true) {
                count++;
            }
        }
    }
    return count;
}

export const validateAction = ({ role, subscriptionPlan = null, scenesObj = [], action = false, extraLocations = 0 }) => {
    const constraints = roleConstraints(role.toLowerCase(), subscriptionPlan);

    if (!action || !constraints) return { state: false, limit: 'no action or valid role' };

    if (role.toLowerCase() === adminRole) {
        return { state: true, limit: 1000000 };
    }

    const urlParams = new URLSearchParams(window.location.search);
    const roomID = urlParams.get('id');
    const locationID = urlParams.get('loc');

    const scenes = localStorage.getItem(`publish_${roomID}`) ? JSON.parse(localStorage.getItem(`publish_${roomID}`)).scenes : scenesObj;

    switch (action) {
        case 'rooms': return { state: true, limit: 1000000 };
        case 'locations': return { state: constraints.locations === UNLIMITED || (scenes.length + extraLocations) < constraints.locations, limit: constraints.locations };
        case 'publicStage':
            rolesWithDisabledToggles.includes(role) && $("#publicStage").prop("checked", false);
            if (typeof constraints.publicStage === 'boolean') {
                return { state: constraints.publicStage, limit: constraints.publicStage };
            } else {
                return { state: countStates('publicStage', scenes) < constraints[action], limit: constraints[action] };
            }
        case 'paidLocation':
            rolesWithDisabledToggles.includes(role) && $("#locationWithPayment").prop("checked", false);
            if (typeof constraints.paidLocation === 'boolean') {
                return { state: constraints.paidLocation, limit: constraints.paidLocation };
            } else {
                return { state: countStates('locationWithPayment', scenes) < constraints[action], limit: constraints[action] };
            }
        default:
            if (typeof constraints[action] === 'boolean') {
                return { state: constraints[action], limit: constraints[action] };
            } else {
                let sceneObjects = [];
                for (let x = 0; x < scenes.length; x++) {
                    if (scenes[x].id === locationID) {
                        sceneObjects = scenes[x].objects;
                        break;
                    }
                }
                return { state: countSceneElements(action, sceneObjects) < constraints[action], limit: constraints[action] };
            }
    }

}

export const validateRoomItem = (spaceType, item) => {
    if (spaceType.toLowerCase() === "premium") {
        return true;
    }
    if (spaceType.toLowerCase() === "public") {
        return false;
    }
    if (item.type === EDITOR_ACTIONS.public_space) {
        return false;
    }
    if (item.type === EDITOR_ACTIONS.youtube_element) {
        return false;
    }
    if (item.type === EDITOR_ACTIONS.paid_space) {
        return false;
    }
    if (item.type === EDITOR_ACTIONS.welcome_screen) {
        return false;
    }
    if (item.type === EDITOR_ACTIONS.hide_from_space_browser) {
        return false;
    }
}

export const canIUseFeature = (features, featureName) => {
    if (features[featureName]) {
        return true;
    }
    return false;
}
